import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../../../components/AuthContext';
import { FaDownload, FaPrint, FaUser, FaRupeeSign, FaFileAlt, FaChartPie, FaList, FaInfoCircle, FaEye } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getCurrentMonthDates } from '../../../utils/dateUtils';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
} from 'chart.js';
import { Pie } from 'react-chartjs-2';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
);

const QuotationsReports = () => {
  const { user } = useContext(AuthContext);
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [dateFilter, setDateFilter] = useState(getCurrentMonthDates());
  const [salesPersonFilter, setSalesPersonFilter] = useState('');
  const [salesPersons, setSalesPersons] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [summary, setSummary] = useState({
    totalQuotations: 0,
    totalAmount: 0,
    employeeContribution: [],
    statusContribution: []
  });
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [showQuotationModal, setShowQuotationModal] = useState(false);
  const [quotationDetails, setQuotationDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    fetchQuotations();
    fetchSalesPersons();
  }, []);

  const fetchSalesPersons = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (user?.user?.company?._id) {
        params.append('companyId', user.user.company._id);
      } else if (user?.user?.company) {
        params.append('companyId', user.user.company);
      }
      
      const response = await axios.get(`${API_BASE}/reports/quotations/employees?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSalesPersons(response.data.employees || []);
    } catch (error) {
      console.error('Error fetching sales persons:', error);
    }
  };

  const fetchQuotations = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (dateFilter.startDate) params.append('startDate', dateFilter.startDate);
      if (dateFilter.endDate) params.append('endDate', dateFilter.endDate);
      if (salesPersonFilter) params.append('salesPerson', salesPersonFilter);
      
      // Company filtering
      if (user?.user?.company?._id) {
        params.append('companyId', user.user.company._id);
      } else if (user?.user?.company) {
        params.append('companyId', user.user.company);
      }

      const response = await axios.get(`${API_BASE}/reports/quotations?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setQuotations(response.data.quotations || []);
      setTotalAmount(response.data.summary?.totalAmount || 0);
      setSummary(response.data.summary || {
        totalQuotations: 0,
        totalAmount: 0,
        employeeContribution: [],
        statusContribution: []
      });
    } catch (error) {
      console.error('Error fetching quotations:', error);
      toast.error('Failed to fetch quotation reports');
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    fetchQuotations();
  };

  const handlePrintQuotation = (quotation) => {
    // Generate proper PDF print URL based on quotation type
    let printUrl = '';
    
    if (quotation.quotationType === 'QuotationNew') {
      // For QuotationNew, use the rlugroup.hbs template
      const apiEndpoint = `/quotation-new/data/${quotation._id}`;
      printUrl = `https://rlugroup.in/pdf/convert?fileName=rlugroup.hbs&url=${process.env.REACT_APP_API_BASE_URL}${apiEndpoint}`;
    } else if (quotation.quotationType === 'TallyQuotation') {
      // For TallyQuotation, use the rlugroup-tally-quotation.hbs template with full print type
      printUrl = `https://rlugroup.in/pdf/convert?fileName=rlugroup-tally-quotation.hbs&url=${process.env.REACT_APP_API_BASE_URL}/tally-quotation/print/${quotation._id}?printType=full`;
    } else {
      // Fallback for other quotation types or legacy quotations
      printUrl = `https://rlugroup.in/pdf/convert?fileName=rlugroup.hbs&url=${process.env.REACT_APP_API_BASE_URL}/quotation-new/data/${quotation._id}`;
    }
    
    // Open the PDF in a new tab
    window.open(printUrl, '_blank');
  };

  const handleViewQuotationDetails = async (quotation) => {
    setLoadingDetails(true);
    setSelectedQuotation(quotation);
    setShowQuotationModal(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE}/reports/quotations/details/${quotation._id}?type=${quotation.quotationType}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setQuotationDetails(response.data);
    } catch (error) {
      console.error('Error fetching quotation details:', error);
      toast.error('Failed to fetch quotation details');
      setQuotationDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const companyId = user?.user?.company;
      
      console.log('Export - User context:', user);
      console.log('Export - Company ID:', companyId);
      
      // Create descriptive filename with date range and filters
      const startDateStr = dateFilter.startDate ? new Date(dateFilter.startDate).toISOString().split('T')[0] : 'all';
      const endDateStr = dateFilter.endDate ? new Date(dateFilter.endDate).toISOString().split('T')[0] : 'all';
      const salesPersonName = salesPersons.find(sp => sp._id === salesPersonFilter)?.name || 'all-salespeople';
      const timestamp = new Date().toISOString().split('T')[0];
      
      const filename = `quotations-comprehensive-report_${startDateStr}-to-${endDateStr}_${salesPersonName}_${timestamp}.csv`;

      const exportData = {
        startDate: dateFilter.startDate,
        endDate: dateFilter.endDate,
        salesPerson: salesPersonFilter,
        companyId: companyId
      };
      
      console.log('Export - Sending data:', exportData);

      const response = await axios.post(`${API_BASE}/reports/quotations/export`, exportData, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Export completed successfully!');
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export data');
    }
  };

    const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  // Helper function to format salesmen names
  const formatSalesmenNames = (salesmen) => {
    if (!salesmen || salesmen.length === 0) {
      return 'N/A';
    }
    
    // If salesmen is an array of objects with name property
    if (Array.isArray(salesmen)) {
      const names = salesmen.map(salesman => 
        typeof salesman === 'object' ? (salesman.name || salesman.firstName || 'Unknown') : salesman
      ).filter(name => name && name !== 'Unknown');
      
      if (names.length === 0) return 'N/A';
      if (names.length === 1) return names[0];
      if (names.length === 2) return names.join(' & ');
      return `${names[0]} & ${names.length - 1} others`;
    }
    
    // If salesmen is a single object
    if (typeof salesmen === 'object') {
      return salesmen.name || salesmen.firstName || 'N/A';
    }
    
    return salesmen || 'N/A';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  // Chart colors
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658'];

  // Prepare chart data for react-chartjs-2
  const employeeChartData = {
    labels: summary.employeeContribution?.map(emp => emp.name) || [],
    datasets: [
      {
        data: summary.employeeContribution?.map(emp => emp.totalAmount) || [],
        backgroundColor: COLORS,
        borderColor: COLORS.map(color => color + '80'),
        borderWidth: 1,
      }
    ]
  };

  const statusChartData = {
    labels: summary.statusContribution?.map(status => status.status) || [],
    datasets: [
      {
        data: summary.statusContribution?.map(status => status.totalAmount) || [],
        backgroundColor: COLORS,
        borderColor: COLORS.map(color => color + '80'),
        borderWidth: 1,
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          usePointStyle: true,
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = formatCurrency(context.parsed);
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((context.parsed / total) * 100).toFixed(1);
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Quotations Reports</h2>
        <p className="text-gray-600">View and analyze quotations issued by date and sales person</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4">Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
            <input
              type="date"
              value={dateFilter.startDate}
              onChange={(e) => setDateFilter({...dateFilter, startDate: e.target.value})}
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
            <input
              type="date"
              value={dateFilter.endDate}
              onChange={(e) => setDateFilter({...dateFilter, endDate: e.target.value})}
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sales Person</label>
            <select
              value={salesPersonFilter}
              onChange={(e) => setSalesPersonFilter(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
            >
              <option value="">All Sales Persons</option>
              {salesPersons.map(person => (
                <option key={person._id} value={person._id}>
                  {person.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end space-x-2">
            <button
              onClick={handleFilter}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
            >
              <FaEye className="mr-2" />
              Filter
            </button>
            <button
              onClick={handleExport}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center relative group"
              title="Export comprehensive quotations report with all details"
            >
              <FaDownload className="mr-2" />
              Export Complete Report
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                Export with all quotation details, customer info, financial breakdown & print URLs
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Analytics Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Employee-wise Contribution */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <FaChartPie className="mr-2 text-blue-500" />
            Employee-wise Contribution
          </h3>
          {summary.employeeContribution?.length > 0 ? (
            <div>
              <div className="h-64 mb-4">
                <Pie data={employeeChartData} options={chartOptions} />
              </div>
              <div className="space-y-2">
                {summary.employeeContribution.map((employee, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      ></div>
                      <span>{employee.name}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">{formatCurrency(employee.totalAmount)}</div>
                      <div className="text-gray-500">{employee.count} quotations</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              No data available
            </div>
          )}
        </div>

        {/* Status-wise Contribution */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <FaChartPie className="mr-2 text-green-500" />
            Status-wise Contribution
          </h3>
          {summary.statusContribution?.length > 0 ? (
            <div>
              <div className="h-64 mb-4">
                <Pie data={statusChartData} options={chartOptions} />
              </div>
              <div className="space-y-2">
                {summary.statusContribution.map((status, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      ></div>
                      <span className="capitalize">{status.status}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">{formatCurrency(status.totalAmount)}</div>
                      <div className="text-gray-500">{status.count} quotations</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              No data available
            </div>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <FaFileAlt className="text-blue-500 text-2xl mr-3" />
            <div>
              <p className="text-sm text-gray-600">Total Quotations</p>
              <p className="text-2xl font-bold text-gray-800">{quotations.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <FaRupeeSign className="text-green-500 text-2xl mr-3" />
            <div>
              <p className="text-sm text-gray-600">Total Amount</p>
              <p className="text-2xl font-bold text-gray-800">{formatCurrency(totalAmount)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <FaUser className="text-purple-500 text-2xl mr-3" />
            <div>
              <p className="text-sm text-gray-600">Average per Quotation</p>
              <p className="text-2xl font-bold text-gray-800">
                {formatCurrency(quotations.length > 0 ? totalAmount / quotations.length : 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold">Quotations</h3>
        </div>
        
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Loading quotations...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quotation No
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sales Person
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {quotations.length > 0 ? (
                  quotations.map((quotation, index) => (
                    <tr key={quotation._id || index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(quotation.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {quotation.quotationType || 'Quotation'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                        {quotation.quotationId || quotation.id || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatSalesmenNames(quotation.salesmen)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {quotation.recipient?.name || quotation.recipient?.firmName || quotation.recipient?.businessName || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatCurrency(quotation.totalAmount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(quotation.status)}`}>
                          {quotation.status || 'Pending'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <button
                          onClick={() => handlePrintQuotation(quotation)}
                          className="text-green-600 hover:text-green-900 mr-3"
                          title="Print Quotation"
                        >
                          <FaPrint />
                        </button>
                        <button
                          onClick={() => handleViewQuotationDetails(quotation)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Reminders"
                        >
                          <FaEye />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                      No quotations found for the selected criteria
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Quotation Reminders Modal */}
      {showQuotationModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                Quotation Reminders - {selectedQuotation?.quotationId || selectedQuotation?.id}
              </h3>
              <button
                onClick={() => {
                  setShowQuotationModal(false);
                  setSelectedQuotation(null);
                  setQuotationDetails(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {loadingDetails ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-gray-600">Loading details...</p>
              </div>
            ) : quotationDetails ? (
              <div className="space-y-6">
                {/* Reminders */}
                {quotationDetails.reminders && quotationDetails.reminders.length > 0 ? (
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-3">Related Reminders</h4>
                    <div className="space-y-3">
                      {quotationDetails.reminders.map((reminder, index) => (
                        <div key={index} className="border rounded-lg p-3">
                          <div className="flex justify-between items-start mb-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(reminder.status)}`}>
                              {reminder.status}
                            </span>
                            <span className="text-xs text-gray-500">{formatDate(reminder.date)}</span>
                          </div>
                          <p className="text-sm text-gray-700">{reminder.message}</p>
                          {reminder.details && (
                            <div className="mt-2 text-xs text-gray-600">
                              <pre className="whitespace-pre-wrap">{JSON.stringify(reminder.details, null, 2)}</pre>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No reminders found for this quotation
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                Failed to load quotation details
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotationsReports;
